import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2, Sparkles, Download, ArrowRight } from "lucide-react";
import { FOUNDATION } from "@/data/content";
import SeoHead from "@/components/common/SeoHead";
import { getImageUrl } from "@/lib/api";

export default function ProgramDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/programs/${slug}`)
      .then((r) => setProgram(r.data))
      .catch(() => setProgram(null))
      .finally(() => setLoading(false));
    api.get("/programs").then((r) => setOthers((r.data.items || []).filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (loading) return <div className="container-sf py-40 text-center text-slate-500">Memuat program...</div>;
  if (!program) return (
    <div className="container-sf py-40 text-center">
      <h1 className="text-3xl font-bold text-slate-900">Program tidak ditemukan</h1>
      <Link to="/program" className="btn-primary mt-6">Kembali ke Program</Link>
    </div>
  );

  return (
    <div data-testid={`page-program-${slug}`}>
      <SeoHead
        title={program.title}
        description={program.short_description}
        image={program.hero_image}
      />
      {/* Hero */}
      <section className="relative pt-14 pb-16 md:pt-24 md:pb-24 overflow-hidden bg-gradient-to-br from-[#0B1A3D] via-[var(--brand-blue)] to-[#254DAA] text-white">
        <div className="grain-overlay" />
        <div className="container-sf relative">
          <button onClick={() => navigate("/program")} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-8">
            <ArrowLeft className="h-4 w-4" /> Semua Program
          </button>
          <div className="grid lg:grid-cols-[1.5fr,1fr] gap-10 items-center">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--brand-orange)] mb-4">{program.category}</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-white">{program.title}</h1>
              <p className="mt-5 text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl">{program.short_description}</p>
            </div>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl hidden lg:block">
              <img src={getImageUrl(program.hero_image)} alt={program.title} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-y bg-white">
        <div className="container-sf">
          <div className="grid lg:grid-cols-[2fr,1fr] gap-12">
            <div className="space-y-14">
              <div>
                <div className="eyebrow mb-3">Tentang Program</div>
                <h2 className="text-3xl font-bold text-slate-900 mb-5">Deskripsi</h2>
                <div
                  className="prose prose-slate max-w-none prose-p:text-slate-700 prose-p:leading-relaxed prose-headings:text-slate-900 prose-a:text-[var(--brand-blue)]"
                  dangerouslySetInnerHTML={{ __html: program.description || "" }}
                />
              </div>

              {program.objectives?.length > 0 && (
                <div>
                  <div className="eyebrow mb-3">Tujuan</div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-5">Sasaran Program</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {program.objectives.map((o, i) => (
                      <div key={i} className="card-sf p-5 flex gap-3">
                        <CheckCircle2 className="h-5 w-5 text-[var(--brand-orange)] shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700 leading-relaxed">{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {program.activities?.length > 0 && (
                <div>
                  <div className="eyebrow mb-3">Aktivitas</div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-5">Kegiatan Utama</h2>
                  <ol className="space-y-4">
                    {program.activities.map((a, i) => (
                      <li key={i} className="flex gap-5">
                        <span className="marker-num text-3xl leading-none shrink-0 w-12">0{i + 1}</span>
                        <div className="pt-1.5 text-slate-700 leading-relaxed">{a}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="space-y-6 lg:sticky lg:top-28 self-start">
              {program.sub_programs?.length > 0 && (
                <div className="card-sf p-6" data-testid="program-sub-list">
                  <div className="eyebrow mb-3">Sub Program</div>
                  <h3 className="text-lg font-bold text-slate-900 mb-4">{program.sub_programs.length} Program Turunan</h3>
                  <ul className="space-y-2">
                    {program.sub_programs.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700 leading-relaxed">
                        <Sparkles className="h-3.5 w-3.5 text-[var(--brand-orange)] shrink-0 mt-1" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="rounded-2xl bg-[var(--brand-blue)] text-white p-7">
                <h3 className="text-xl font-bold mb-2">Dukung Program Ini</h3>
                <p className="text-sm text-slate-200 leading-relaxed">Anda dapat mendukung program ini melalui donasi atau menjadi relawan.</p>
                <div className="mt-5 flex flex-col gap-2">
                  <Link to="/donasi" className="btn-primary justify-center !py-3 !text-sm">Donasi Sekarang</Link>
                  <Link to="/volunteer" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 transition">Jadi Relawan</Link>
                </div>
              </div>

              <a href={FOUNDATION.companyProfilePdf} target="_blank" rel="noreferrer" className="card-sf p-6 flex items-center gap-4 hover:border-[var(--brand-orange)]/50">
                <div className="h-12 w-12 rounded-xl bg-[var(--brand-soft-blue)] flex items-center justify-center text-[var(--brand-blue)]">
                  <Download className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Company Profile</div>
                  <div className="text-xs text-slate-500">Unduh PDF resmi</div>
                </div>
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Programs */}
      {others.length > 0 && (
        <section className="section-y bg-[var(--bg)]">
          <div className="container-sf">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8">Program lain yang mungkin menarik</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {others.map((o) => (
                <Link key={o.slug} to={`/program/${o.slug}`} className="card-sf overflow-hidden group flex flex-col" data-testid={`related-${o.slug}`}>
                  <div className="aspect-[4/2.5] overflow-hidden bg-slate-100">
                    <img src={getImageUrl(o.hero_image)} alt={o.title} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{o.category}</div>
                    <h4 className="mt-1 text-lg font-bold text-slate-900 leading-tight">{o.title}</h4>
                    <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-[var(--brand-blue)] group-hover:text-[var(--brand-orange)]">
                      Baca <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
